import { jsPDF } from "jspdf";
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { Acorn } from '../../models/acorn';
// Project logo to render on each coupon (right of the QR)
// Vite will resolve this to a URL at build time
import logoUrl from '../../assets/logo.png';

type Options = {
  isGrouped?: boolean;
  returnBase64?: boolean;
  returnBlob?: boolean;
};

export async function generateCouponPDF(acorns: Acorn[], opts: Options = {}): Promise<string | Blob | void> {
    const { isGrouped = false, returnBase64 = false, returnBlob = false } = opts;

    // Helper to generate QR Code from a string
    const generateQRCode = async (code: string): Promise<string> => {
        try {
            return await QRCode.toDataURL(code, {
                errorCorrectionLevel: 'M',
                margin: 1,
                width: 100,
                color: {
                    dark: '#000000',
                    light: '#ffffff',
                },
            });
        } catch (err) {
            console.error('QR Code generation error:', err);
            throw err;
        }
    };

    // Helper to load an image element from a URL (for jsPDF addImage)
    const loadImage = (src: string): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(e);
            img.src = src;
        });

    try {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'in',
            format: [8.5, 11],
        });

        // Pre-load logo once (use a tiny size on each coupon)
        let logoImg: HTMLImageElement | null = null;
        try {
            logoImg = await loadImage(logoUrl as unknown as string);
        } catch {
            // Non-fatal: continue without a logo
            logoImg = null;
        }

        // Define coupon dimensions (in inches)
        const couponWidth = 4;
        const couponHeight = 2;
        const margin = 0.25;
        const columns = 2;
        const rows = 5;

        // Calculate starting positions
        const startX = (8.5 - columns * couponWidth) / 2;
        const startY = (11 - rows * couponHeight) / 2;

        // Calculate total pages needed (10 coupons per page)
        const totalPages = Math.ceil(acorns.length / (columns * rows));

        for (let page = 0; page < totalPages; page++) {
            if (page > 0) {
                doc.addPage();
            }

            // Draw coupons for the current page
            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < columns; col++) {
                    const index = page * columns * rows + row * columns + col;
                    if (index >= acorns.length) continue;

                    const acorn = acorns[index];
                    const x = startX + col * couponWidth;
                    const y = startY + row * couponHeight;

                    // Draw a dotted border for the coupon
                    doc.setDrawColor(0, 0, 0);
                    doc.setLineWidth(0.01);
                    doc.setLineDashPattern([0.05, 0.05], 0);
                    doc.rect(x, y, couponWidth - margin / 2, couponHeight - margin / 2, 'S');

                    // Generate and add the QR code based on the acorn code
                    const qrCodeDataUrl = await generateQRCode(acorn.code);
                    const qrSize = 1.25;
                    // Try to center QR under the code text width
                    const codeFontSize = 12;
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(codeFontSize);
                    const codeTextWidth = doc.getTextWidth(acorn.code);
                    const codeLeft = x + 0.25;
                    const codeCenter = codeLeft + codeTextWidth / 2;
                    let qrX = codeCenter - qrSize / 2 - 0.06; // slight left bias
                    // Clamp inside coupon interior
                    const minQrX = x + 0.15; // allow a bit closer to the left border
                    const maxQrX = x + (couponWidth - margin / 2) - qrSize - 0.25;
                    qrX = Math.max(minQrX, Math.min(maxQrX, qrX));
                    const qrY = y + 0.26; // slightly higher under the code line
                    doc.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

                    // Add the coupon code text smaller and left-aligned, near the top-left
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(codeFontSize);
                    doc.text(acorn.code, codeLeft, y + 0.28);

                    // Right side content: logo + description on the same row (closer to QR)
                    const rightStartX = qrX + qrSize + 0.10; // tighter gap after QR
                    const rightWidth = (x + (couponWidth - margin / 2)) - rightStartX - 0.15; // leave a small right padding

                    // Small logo
                    let logoHeight = 0.33; // slightly smaller
                    let logoWidth = 0.33;
                    // vertically center with QR
                    const centerY = qrY + qrSize / 2;
                    const logoY = centerY - logoHeight / 2;
                    if (logoImg) {
                        logoWidth = (logoImg.width / logoImg.height) * logoHeight;
                        doc.addImage(logoImg, 'PNG', rightStartX, logoY, logoWidth, logoHeight);
                    }

                    // Description text to the right of the icon (single line, trimmed)
                    let description = acorn.acornDescription?.trim() || '';
                    const textX = rightStartX + (logoImg ? logoWidth + 0.03 : 0);
                    const textMaxWidth = rightWidth - (logoImg ? logoWidth + 0.12 : 0);
                    if (description) {
                        // Target ~48 chars; also ensure it fits the available width
                        const approxMaxChars = 48;
                        if (description.length > approxMaxChars) {
                            description = description.slice(0, approxMaxChars - 1).trimEnd() + '…';
                        }
                        doc.setFont('times', 'normal');
                        doc.setFontSize(10); // slightly smaller
                        doc.setTextColor(20, 20, 20);
                        const oneLine = (doc.splitTextToSize(description, textMaxWidth) as string[])[0] || '';
                        const textY = centerY + 0.05; // align near vertical center
                        doc.text(oneLine, textX, textY);
                    }

                    // Footer recycle note (two lines) placed just below the QR code
                    const footer1 = 'Recycle This Coupon For A Chance To Win Additional Rewards.';
                    const footer2 = 'Recycle At: Puma Lounge (Room 202) Or Your Teacher';
                    doc.setFont('times', 'normal');
                    doc.setFontSize(8.5);
                    doc.setTextColor(30, 30, 30);
                    const footerY1 = qrY + qrSize + 0.07; // slightly lower than before
                    const footerY2 = footerY1 + 0.16;
                    const centerX = x + (couponWidth - margin / 2) / 2;
                    doc.text(footer1, centerX, footerY1, { align: 'center' });
                    doc.text(footer2, centerX, footerY2, { align: 'center' });
                }
            }
        }

        // Decide what the caller wants back
        if (returnBase64) {
            return doc.output("datauristring");
        }

        if (returnBlob) {
            return doc.output("blob");
        }

        // Default behaviour = download, dynamic file name based on isGrouped
        doc.save(isGrouped ? `coupons-${Date.now()}.pdf` : `${acorns[0]?.code || "coupon"}.pdf`);
        toast.success('PDF generated successfully');
    } catch (error) {
        console.error('PDF generation error:', error);
        toast.error('Failed to generate PDF');
    }
}
